<script lang="ts">
  import { competitorServiceOptionsStore } from '@entities/competitor-service/store.svelte';
  import { filterProductCompetitorCandidates } from '@domain/competitor-service/product-competitors-edit';
  import {
    listProductCompetitorServices,
    relinkProductCompetitorService,
  } from '@shared/api/products';
  import { ApiError } from '@shared/api/client';
  import Modal from '@shared/ui/Modal.svelte';
  import type { CompetitorService } from '@shared/types/competitor-service';
  import type {
    ProductCompetitorService,
    UnlinkedProductCompetitorService,
  } from '@shared/types/product-competitor-service';

  interface Props {
    open: boolean;
    row: UnlinkedProductCompetitorService | null;
    canEdit: boolean;
    onClose: () => void;
    onRelinked: (row: ProductCompetitorService) => void;
    /** `null` means a newer global snapshot superseded this refresh. */
    onConflict: (rowId: string) => Promise<boolean | null>;
  }

  type ConflictRefreshResult =
    | { kind: 'resolved'; row: ProductCompetitorService }
    | { kind: 'duplicate' }
    | { kind: 'unchanged' }
    | { kind: 'failed' };

  let { open, row, canEdit, onClose, onRelinked, onConflict }: Props = $props();

  let searchQuery = $state('');
  let selectedId = $state('');
  let step = $state<'select' | 'confirm'>('select');
  let existingRows = $state<ProductCompetitorService[]>([]);
  let existingRowsLoading = $state(false);
  let existingRowsError = $state<string | null>(null);
  let submitting = $state(false);
  let serverError = $state<string | null>(null);
  let activeRowId = $state<string | null>(null);
  let loadGeneration = 0;

  let candidateLoading = $derived(
    existingRowsLoading || competitorServiceOptionsStore.loading,
  );
  let candidateError = $derived(
    existingRowsError ?? competitorServiceOptionsStore.error,
  );
  let candidates = $derived.by(() =>
    filterProductCompetitorCandidates(
      competitorServiceOptionsStore.data,
      existingRows,
      searchQuery,
    ),
  );
  let selectedCandidate = $derived<CompetitorService | null>(
    competitorServiceOptionsStore.data.find(
      (candidate) => candidate.id === selectedId,
    ) ?? null,
  );

  $effect(() => {
    const currentRow = open && canEdit ? row : null;
    if (currentRow === null) {
      activeRowId = null;
      loadGeneration += 1;
      return;
    }
    if (activeRowId === currentRow.id) return;
    activeRowId = currentRow.id;
    resetState();
    void loadCandidateData(currentRow);
  });

  $effect(() => {
    if (
      selectedId !== '' &&
      step === 'select' &&
      !candidateLoading &&
      candidateError === null &&
      !candidates.some((candidate) => candidate.id === selectedId)
    ) {
      selectedId = '';
      step = 'select';
    }
  });

  function resetState(): void {
    searchQuery = '';
    selectedId = '';
    step = 'select';
    existingRows = [];
    existingRowsError = null;
    serverError = null;
  }

  function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
  }

  async function loadCandidateData(
    targetRow: UnlinkedProductCompetitorService,
  ): Promise<void> {
    const generation = ++loadGeneration;
    existingRowsLoading = true;
    existingRowsError = null;
    serverError = null;

    const [existingResult] = await Promise.allSettled([
      listProductCompetitorServices(targetRow.product_id),
      competitorServiceOptionsStore.load(),
    ]);
    if (generation !== loadGeneration || activeRowId !== targetRow.id) return;

    if (existingResult.status === 'fulfilled') {
      existingRows = existingResult.value.items;
    } else {
      existingRows = [];
      existingRowsError = errorMessage(
        existingResult.reason,
        'この自社ソリューションの紐付け状況を読み込めませんでした。',
      );
    }
    existingRowsLoading = false;
  }

  async function retryCandidates(): Promise<void> {
    if (submitting || row === null || !canEdit) return;
    await loadCandidateData(row);
  }

  function close(): void {
    if (submitting) return;
    onClose();
  }

  function proceedToConfirmation(): void {
    if (candidateLoading || candidateError !== null || !selectedCandidate) {
      serverError = '候補から外部ソリューションを選択してください。';
      return;
    }
    serverError = null;
    step = 'confirm';
  }

  function returnToSelection(): void {
    if (submitting) return;
    serverError = null;
    step = 'select';
  }

  async function relink(): Promise<void> {
    if (submitting || !canEdit || row === null || selectedCandidate === null) {
      return;
    }

    const targetRow = row;
    const targetCandidate = selectedCandidate;
    submitting = true;
    serverError = null;
    try {
      const updated = await relinkProductCompetitorService(
        targetRow.product_id,
        targetRow.id,
        { competitor_service_id: targetCandidate.id },
      );
      if (row?.id !== targetRow.id) return;
      onRelinked(updated);
    } catch (error) {
      if (row?.id !== targetRow.id) return;
      if (error instanceof ApiError && error.status === 409) {
        let stillUnlinked: boolean | null;
        try {
          stillUnlinked = await onConflict(targetRow.id);
        } catch {
          serverError =
            '更新が競合しました。選択内容は保持しています。最新の未紐付け状況を確認できなかったため、時間をおいて再試行してください。';
          return;
        }
        if (stillUnlinked === null) return;
        if (!stillUnlinked) {
          onClose();
          return;
        }
        if (row?.id !== targetRow.id) return;
        const conflict = await refreshExistingRowsAfterConflict(
          targetRow,
          targetCandidate.id,
        );
        if (row?.id !== targetRow.id) return;
        if (conflict.kind === 'resolved') {
          onRelinked(conflict.row);
          return;
        }
        if (conflict.kind === 'duplicate') {
          selectedId = '';
          step = 'select';
          serverError =
            '選択した外部ソリューションは、この自社ソリューションにすでに紐付いています。別の外部ソリューションを選択してください。';
          return;
        }
        serverError =
          conflict.kind === 'failed'
            ? '更新が競合しました。選択内容は保持しています。最新の紐付け状況を確認できなかったため、時間をおいて再試行してください。'
            : '更新が競合しました。選択内容は保持しています。候補選択に戻って最新の状態を確認するか、再試行してください。';
      } else {
        serverError = errorMessage(
          error,
          '紐付けの更新に失敗しました。選択内容を確認して再試行してください。',
        );
      }
    } finally {
      submitting = false;
    }
  }

  async function refreshExistingRowsAfterConflict(
    targetRow: UnlinkedProductCompetitorService,
    selectedMasterId: string,
  ): Promise<ConflictRefreshResult> {
    try {
      const response = await listProductCompetitorServices(
        targetRow.product_id,
      );
      if (row?.id !== targetRow.id) return { kind: 'failed' };
      existingRows = response.items;

      const refreshedTarget = response.items.find(
        (item) => item.id === targetRow.id,
      );
      if (
        refreshedTarget &&
        refreshedTarget.competitor_service_id !== null
      ) {
        return { kind: 'resolved', row: refreshedTarget };
      }
      if (
        response.items.some(
          (item) =>
            item.id !== targetRow.id &&
            item.competitor_service_id === selectedMasterId,
        )
      ) {
        return { kind: 'duplicate' };
      }
      return { kind: 'unchanged' };
    } catch {
      return { kind: 'failed' };
    }
  }

  function cardTitle(value: string | null | undefined): string {
    return value && value.length > 0 ? value : '（名称なし）';
  }
</script>

<Modal
  open={open && row !== null && canEdit}
  title="未紐付け競合カードを手動で紐付ける"
  onClose={close}
>
  {#if row}
    {#if step === 'select'}
      <div class="card-context" data-testid="relink-source-card">
        <span class="context-label">対象の既存カード</span>
        <strong>{cardTitle(row.title)}</strong>
        <span>{row.product_name}</span>
      </div>

      <label class="search-field">
        <span>外部ソリューション名検索</span>
        <input
          type="search"
          placeholder="外部ソリューション名で検索"
          bind:value={searchQuery}
          autocomplete="off"
          disabled={submitting}
          data-testid="unlinked-relink-search"
        />
      </label>

      <section class="candidate-section" aria-label="紐付け先候補">
        <div class="candidate-heading">
          <span>競合ソリューション候補</span>
          <small data-testid="unlinked-relink-candidate-count"
            >{candidates.length}件</small
          >
        </div>

        {#if candidateLoading}
          <p class="state-box" data-testid="unlinked-relink-loading">
            候補を読み込んでいます。
          </p>
        {:else if candidateError}
          <div class="state-box load-error" role="alert">
            <p>{candidateError}</p>
            <button type="button" onclick={() => void retryCandidates()}>
              再試行
            </button>
          </div>
        {:else if candidates.length === 0}
          <p class="state-box" data-testid="unlinked-relink-empty">
            {searchQuery.trim().length > 0
              ? '検索条件に一致する競合ソリューションがありません'
              : '紐付け可能な競合ソリューションがありません'}
          </p>
        {:else}
          <div class="candidate-list" data-testid="unlinked-relink-candidates">
            {#each candidates as candidate (candidate.id)}
              <button
                type="button"
                class:selected={selectedId === candidate.id}
                aria-pressed={selectedId === candidate.id}
                disabled={submitting}
                onclick={() => {
                  selectedId = candidate.id;
                  serverError = null;
                }}
                data-testid="unlinked-relink-candidate"
              >
                <span class="candidate-kind">競合</span>
                <span class="candidate-name">{candidate.name}</span>
              </button>
            {/each}
          </div>
        {/if}
      </section>
    {:else if selectedCandidate}
      <section class="confirmation" data-testid="unlinked-relink-confirmation">
        <p>
          次の既存カードを、選択した外部ソリューションへ紐付けます。内容を確認してください。
        </p>
        <dl>
          <div>
            <dt>自社ソリューション</dt>
            <dd>{row.product_name}</dd>
          </div>
          <div>
            <dt>既存カード名</dt>
            <dd>{cardTitle(row.title)}</dd>
          </div>
          <div class="destination">
            <dt>紐付け先の外部ソリューション</dt>
            <dd>{selectedCandidate.name}</dd>
          </div>
        </dl>
        <p class="confirmation-note">
          既存カードのメモと作成日時はそのまま保持されます。
        </p>
      </section>
    {/if}

    {#if serverError}
      <p class="server-error" role="alert" data-testid="unlinked-relink-error">
        {serverError}
      </p>
    {/if}
  {/if}

  {#snippet footer()}
    {#if step === 'select'}
      <button type="button" onclick={close} disabled={submitting}>
        キャンセル
      </button>
      <button
        type="button"
        class="primary"
        onclick={proceedToConfirmation}
        disabled={candidateLoading || candidateError !== null || !selectedCandidate}
        data-testid="unlinked-relink-next"
      >
        確認へ進む
      </button>
    {:else}
      <button type="button" onclick={returnToSelection} disabled={submitting}>
        候補選択に戻る
      </button>
      <button
        type="button"
        class="primary"
        onclick={() => void relink()}
        disabled={submitting || !selectedCandidate}
        data-testid="unlinked-relink-submit"
      >
        {submitting ? '更新中…' : 'この内容で紐付ける'}
      </button>
    {/if}
  {/snippet}
</Modal>

<style>
  button {
    font: inherit;
  }
  .card-context {
    display: grid;
    gap: 3px;
    margin-bottom: 14px;
    padding: 12px;
    border: 1px solid #dbe3ef;
    border-radius: 6px;
    background: #f8fafc;
    color: #475569;
    font-size: 13px;
  }
  .card-context strong {
    color: #111827;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
  .context-label,
  .search-field > span {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }
  .search-field {
    display: grid;
    gap: 4px;
    margin-bottom: 14px;
  }
  .search-field input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
  }
  .search-field input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
    outline: none;
  }
  .candidate-section {
    display: grid;
    gap: 8px;
  }
  .candidate-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #374151;
    font-size: 13px;
    font-weight: 700;
  }
  .candidate-heading small {
    color: #64748b;
    font-weight: 600;
  }
  .candidate-list {
    display: grid;
    gap: 6px;
    max-height: 360px;
    overflow-y: auto;
  }
  .candidate-list button {
    display: grid;
    gap: 3px;
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
    text-align: left;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    color: #111827;
    cursor: pointer;
  }
  .candidate-list button:hover:not(:disabled),
  .candidate-list button.selected {
    border-color: #1d4ed8;
    background: #eff6ff;
  }
  .candidate-list button.selected {
    box-shadow: 0 0 0 1px #1d4ed8 inset;
  }
  .candidate-list button:focus-visible {
    outline: 2px solid #60a5fa;
    outline-offset: 2px;
  }
  .candidate-kind {
    color: #64748b;
    font-size: 11px;
  }
  .candidate-name {
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  .state-box {
    margin: 0;
    padding: 16px;
    color: #64748b;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
  }
  .load-error {
    display: grid;
    justify-items: center;
    gap: 10px;
    color: #b91c1c;
  }
  .load-error p {
    margin: 0;
  }
  .confirmation {
    display: grid;
    gap: 14px;
  }
  .confirmation > p {
    margin: 0;
    color: #475569;
    font-size: 13px;
  }
  .confirmation dl {
    display: grid;
    gap: 8px;
    margin: 0;
  }
  .confirmation dl > div {
    display: grid;
    gap: 3px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
  }
  .confirmation dl > div.destination {
    border-color: #93c5fd;
    background: #eff6ff;
  }
  .confirmation dt {
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
  }
  .confirmation dd {
    margin: 0;
    color: #111827;
    font-size: 14px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  .confirmation .confirmation-note {
    color: #64748b;
  }
  .server-error {
    margin: 14px 0 0;
    color: #b91c1c;
    font-size: 13px;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
  }
  button:disabled,
  input:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
</style>
